import Request from "../Request";
import { SaintBernardExportRaw } from "../../interfaces/reports/SaintBernardExport.interface";
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

class CustomReportService {
    static async getSaintBernardExportData(clientId: string | number): Promise<SaintBernardExportRaw[]> {
        try {
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/saint-bernard-export`);
            // API returns { data: [...] }
            const data = response?.data || [];
            return data as SaintBernardExportRaw[];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    static async getCustomGoalsReportData({ clientId, epochMonth }: { clientId: string | number; epochMonth: number; }): Promise<any[]> {
        try {
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/custom-goals`, { epochMonth });
            const data = response?.data || [];
            return data as any[];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    static async getMRFCustomReportData({ clientId, epochMonth }: { clientId: string | number; epochMonth: number; }): Promise<any[]> {
        try {
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/mrf-report`, { epochMonth });
            const data = response?.data || [];
            return data as any[];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    static async getMKECustomReportData({ clientId, epochMonth }: { clientId: string | number; epochMonth: number; }): Promise<any[]> {
        try {
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/mke-report`, { epochMonth });
            const data = response?.data || [];
            return data as any[];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    static async getWholeEarthCustomReportData({ clientId, epochMonth }: { clientId: string | number; epochMonth: number; }): Promise<any[]> {
        try {
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/whole-earth-report`, { epochMonth });
            const data = response?.data || [];
            return data as any[];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    static async getTootsiesClassPerformance({ clientId, startEpochMonth, endEpochMonth, isMtd = false }: { clientId: string | number; startEpochMonth: number | null; endEpochMonth: number | null; isMtd?: boolean; }): Promise<any> {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
                isMtd: isMtd ? 1 : 0,
            };
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/tootsies/class-performance`, params);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    static async getTootsiesInventoryPerformance({ clientId, startEpochMonth, endEpochMonth, isMtd = false }: { clientId: string | number; startEpochMonth: number | null; endEpochMonth: number | null; isMtd?: boolean; }): Promise<any> {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
                isMtd: isMtd ? 1 : 0,
            };
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/tootsies/inventory-performance`, params);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    static async getTootsiesGlobalAnalytics({ clientId, startEpochMonth = null, endEpochMonth = null, monthsBack = 3 }: { clientId: string | number; startEpochMonth: number | null; endEpochMonth: number | null; monthsBack?: number; }): Promise<any> {
        try {
            const params = {
                startEpochMonth: startEpochMonth ?? null,
                endEpochMonth: endEpochMonth ?? null,
                monthsBack,
            };
            const response = await Request.get(`/api/clients/${clientId}/custom-reports/tootsies/global-analytics`, params);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    // Wide-table PDF export helper for custom reports
    static async tableToPdfWide({
        columns,
        data,
        fileName,
        title,
        subtitle,
        orientation = 'l',
        pageWidthPt = 1600,
        pageHeightPt = 1000,
        fontSizePt = 7,
        minColWidthPt = 40,
        maxColWidthPt = 220,
    }: {
        columns: Array<string>;
        data: Array<Array<string | number>>;
        fileName: string;
        title?: string;
        subtitle?: string;
        orientation?: 'p' | 'l';
        pageWidthPt?: number;
        pageHeightPt?: number;
        fontSizePt?: number;
        minColWidthPt?: number;
        maxColWidthPt?: number;
    }) {
        try {
            const doc = new jsPDF({ orientation, unit: 'pt', format: [pageWidthPt, pageHeightPt] });
            const marginLeft = 20;
            const marginRight = 20;
            let startY = 40;

            if (title) {
                doc.setFontSize(20);
                doc.text(title, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 28;
            }
            if (subtitle) {
                doc.setFontSize(14);
                doc.text(subtitle, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 26;
            }

            const pageWidth = doc.internal.pageSize.getWidth();
            const printableWidth = Math.max(0, Math.floor(pageWidth - marginLeft - marginRight - 1));
            const numColumns = Math.max(1, columns.length);

            doc.setFontSize(fontSizePt);
            const minColWidth = Math.max(10, Math.floor(minColWidthPt));
            const maxColWidth = Math.min(Math.floor(maxColWidthPt), Math.floor(printableWidth * 0.35));
            const sampleRows = Math.min(25, data.length);
            const measurementPadding = 10;

            const rawWeights: number[] = columns.map((header, colIdx) => {
                const headerText = String(header || '').trim();
                let maxDataTextWidth = 0;
                for (let r = 0; r < sampleRows; r++) {
                    const cell = data[r][colIdx];
                    if (cell === null || cell === undefined) continue;
                    const cellStr = String(cell).trim();
                    if (!cellStr) continue;
                    const w = doc.getTextWidth(cellStr);
                    if (w > maxDataTextWidth) maxDataTextWidth = w;
                }
                const headerWidth = doc.getTextWidth(headerText);
                const base = Math.max(headerWidth, maxDataTextWidth) + measurementPadding;
                return Math.min(Math.max(base, minColWidth), maxColWidth);
            });

            let totalWeight = rawWeights.reduce((a, b) => a + b, 0);
            let columnWidths: number[];
            if (totalWeight === 0) {
                const fallback = Math.floor(printableWidth / numColumns);
                columnWidths = new Array(numColumns).fill(fallback);
            } else {
                columnWidths = rawWeights.map(w => (w / totalWeight) * printableWidth);
                for (let i = 0; i < columnWidths.length; i++) {
                    columnWidths[i] = Math.min(Math.max(columnWidths[i], minColWidth), maxColWidth);
                }
                totalWeight = columnWidths.reduce((a, b) => a + b, 0);
                if (totalWeight !== printableWidth && totalWeight > 0) {
                    const scale = printableWidth / totalWeight;
                    for (let i = 0; i < columnWidths.length; i++) {
                        columnWidths[i] = Math.min(Math.max(columnWidths[i] * scale, minColWidth), maxColWidth);
                    }
                }
                let intTotal = 0;
                columnWidths = columnWidths.map(w => {
                    const iw = Math.floor(w);
                    intTotal += iw;
                    return iw;
                });
                let remainder = printableWidth - intTotal;
                let idx = 0;
                while (remainder > 0) {
                    if (columnWidths[idx] < maxColWidth) {
                        columnWidths[idx] += 1;
                        remainder--;
                    }
                    idx = (idx + 1) % columnWidths.length;
                }
            }

            autoTable(doc, {
                head: [columns],
                body: data,
                startY: startY,
                margin: { left: marginLeft, right: marginRight },
                styles: {
                    fontSize: fontSizePt,
                    cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
                    overflow: 'linebreak',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.3,
                    lineColor: [180, 180, 180],
                    cellWidth: 'wrap',
                },
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.3,
                    lineColor: [120, 120, 120],
                    fontSize: fontSizePt,
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.3,
                    lineColor: [180, 180, 180],
                    fontSize: fontSizePt,
                },
                tableLineWidth: 0.3,
                tableLineColor: [120, 120, 120],
                tableWidth: printableWidth,
                columnStyles: {
                    ...Object.fromEntries(
                        columns.map((col, index) => [index, { cellWidth: columnWidths[index], overflow: 'linebreak' }])
                    )
                },
                didDrawPage: (dataCtx) => {
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = (pageSize as any).height ? (pageSize as any).height : (pageSize as any).getHeight();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${dataCtx.pageNumber} of ${doc.getNumberOfPages()}`,
                        marginLeft,
                        pageHeight - 10
                    );
                    const date = new Date().toLocaleDateString();
                    doc.text(
                        `Generated on: ${date}`,
                        pageSize.getWidth() - 120,
                        pageHeight - 10
                    );
                }
            });

            const pdfUrl = doc.output('bloburl');
            window.open(pdfUrl, '_blank');
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
}

export default CustomReportService;
